export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  content: string;
  date: string;
  author: string;
  readTime: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "best-ideas-on-highway",
    title: "I Had My Best Ideas on the Highway. Then I'd Ruin Them All.",
    description: "Turns out Einstein, Aaron Sorkin, and I have something in common. How I discovered where my best ideas come from—and why I kept losing them.",
    date: "2025-11-20",
    author: "Joe G.",
    readTime: "4 min read",
    content: `
<p><strong>Turns out Einstein, Aaron Sorkin, and I have something in common. (Besides being geniuses, obviously.)</strong></p>
<p>I was listening to the My First Million PODCAST last week. Sam and Sean talking about how all the great minds had their breakthrough ideas while doing absolutely nothing.</p>
<p>Aaron Sorkin taking 8 showers a day while writing scripts. Einstein floating aimlessly on his motorless boat. Pavel Durov swimming for 4 hours because "you can't be on your phone in the middle of a lake."</p>
<p>And I'm sitting there nodding along because <em>this is me</em>.</p>
<p>My best ideas don't come from staring at my computer. They come on long drives.</p>
<p>Not commutes. Those are too stressful. I'm talking proper long drives. One hour minimum. Country roads. The kind where you put on a podcast and just... think.</p>
<p>That's where I solve problems. That's where ideas actually happen.</p>
<p>No Slack notifications. No email. No ability to "quickly check" anything.</p>
<p>Just me, the road, and whatever podcast is rewiring my neurons.</p>
<h2 id="then-i-d-destroy-everything">Then I'd Destroy Everything</h2>
<p>Here's what would happen:</p>
<p>Idea hits → Hit voice dictate on my phone → Email myself while driving → Another idea → Another email → Another → Another</p>
<p>By the end of a 2-hour drive, I'd have dictated 12 sporadic emails to my work inbox with subject lines like:</p>
<ul>
<li>"App idea"</li>
<li>"Thing for tomorrow"</li>
<li>"Don't forget this"</li>
<li>"IMPORTANT - actually do this one"</li>
</ul>
<p>Then next morning at work, I'd open my inbox and find these buried under 50 other emails. A graveyard of half-formed thoughts that made perfect sense yesterday and now read like a drunk person's shopping list.</p>
<h2 id="the-problem-nobody-solves">The Problem Nobody Solves</h2>
<p>Sam and Sean nailed it on that podcast: <strong>Your brain does its best work when you're in motion or in water.</strong></p>
<p>Which explains why I've solved more problems on long drives than sitting at my desk with an empty Notion doc open for 3 hours.</p>
<p>But here's what the podcast didn't solve: What do you DO with all those ideas?</p>
<p>I needed something dead simple:</p>
<p><strong>While driving:</strong> Idea hits → Capture it (5 seconds) → Back to thinking</p>
<p><strong>Next morning:</strong> One place with everything → Actually execute</p>
<p>That's it. That's the entire feature request.</p>
<h2 id="why-every-other-app-failed">Why Every Other App Failed</h2>
<p><strong>Voice memos?</strong> Terrible for execution. Ever tried working from 12 scattered voice memos?</p>
<p><strong>Email to myself?</strong> Destroys my work inbox. My ideas get buried under actual work emails.</p>
<p><strong>Todoist?</strong> Opens too slowly. Makes me pick a project. Sends marketing emails disguised as task summaries. (I wrote about <a href="/blog/5-todo-apps-i-tried">why Todoist and 4 other apps didn't work for me</a> if you want the full breakdown.)</p>
<p>None of them understood: <strong>My ideas happen in one context (driving) but need to be executed in a different context (tomorrow morning at my desk).</strong></p>
<h2 id="what-i-built-instead">What I Built Instead</h2>
<p>TodoTomorrow works exactly how my brain works:</p>
<p><strong>During the drive:</strong>  </p>
<ul>
<li>Idea hits while listening to podcast</li>
<li>Voice dictate or quick type (5 seconds)</li>
<li>Back to driving/thinking</li>
</ul>
<p><strong>Next morning:</strong>  </p>
<ul>
<li>One email with everything</li>
<li>Clean inbox for actual work emails</li>
<li>Execute instead of sorting</li>
</ul>
<p><strong>Is this oversimplified?</strong></p>
<p>Absolutely. On purpose.</p>
<p><strong>Does it work?</strong></p>
<p>I've captured 159 ideas in 8 weeks. Which is 159 more than I successfully captured with any other system.</p>
<p>Most came from exactly where Einstein and Sorkin said they would: while doing something completely unrelated to work.</p>
<p><strong>Download TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a>.</strong></p>
<p>If your best thinking happens on the highway, this is the app I wish existed when I was ruining all my ideas.</p>
<hr>
<p><em>Building in public at <a href="https://x.com/joegaleckas">@joegaleckas</a>. Helping people stop losing their best ideas to bad capture systems.</em></p>
    `
  },
  {
    slug: "5-todo-apps-i-tried",
    title: "5 Todo Apps I Tried (And Why They Didn't Stick)",
    description: "Todoist, Notion, Google Tasks, and more. Here's why each one failed for me—and what I learned from all of them.",
    date: "2025-11-07",
    author: "Joe G.",
    readTime: "6 min read",
    content: `
<p><strong>Spoiler: The problem wasn&#39;t the apps. It was me trying to force a workflow that didn&#39;t fit.</strong></p>
<p>I have a problem with productivity apps.</p>
<p>I download them with genuine optimism, convinced <em>this</em> will be the one that finally organizes my life. I use it religiously for about a week. Then I quietly go back to scattered notes and half-remembered tasks.</p>
<p>Here are 5 apps I genuinely tried to make work, why they failed for me, and what I learned from each.</p>
<h2 id="1-todoist-the-one-that-s-too-good">1. Todoist: The One That&#39;s Too Good</h2>
<p><strong>What it promises:</strong> Complete task management system with projects, priorities, filters, and natural language processing.</p>
<p><strong>What I experienced:</strong></p>
<p>Week 1: &quot;This is incredible! I can organize everything!&quot;<br>Week 2: &quot;Okay, should this go in Work→Current Projects or Work→Active? And is this P1 or P2?&quot;<br>Week 3: &quot;Why is my daily email full of productivity goals and &#39;open the app&#39; buttons instead of my actual tasks?&quot;<br>Week 4: Back to emailing myself.</p>
<p><strong>The problem:</strong> Todoist is genuinely impressive. But I spent more time organizing my tasks than actually doing them.</p>
<p>Every task became a decision tree:</p>
<ul>
<li>Which project?</li>
<li>Which section within that project?</li>
<li>What priority level?</li>
<li>Add labels?</li>
<li>Set a specific time or just a date?</li>
</ul>
<p>By the time I&#39;d &quot;properly&quot; added the task, I&#39;d forgotten why it mattered.</p>
<p>And their daily digest email? It&#39;s not a task list—it&#39;s gamification badges, usage stats, and marketing dressed up as productivity. You still have to open the app to actually see your tasks.</p>
<p><strong>Who it&#39;s actually for:</strong> People who manage complex projects and genuinely enjoy building systems. My buddy Mike loves it. He has 47 active projects and custom filters for everything. He&#39;s thriving.</p>
<p><strong>Why it didn&#39;t stick for me:</strong> I don&#39;t need a system. I need to remember to buy milk and email Sarah back.</p>
<h2 id="2-notion-the-one-that-became-my-second-job">2. Notion: The One That Became My Second Job</h2>
<p><strong>What it promises:</strong> All-in-one workspace for notes, tasks, databases, and projects.</p>
<p><strong>What I experienced:</strong></p>
<p>Spent 3 hours watching YouTube tutorials on &quot;optimal Notion setups.&quot;</p>
<p>Built an elaborate dashboard with:</p>
<ul>
<li>Task database with 12 properties</li>
<li>Project tracker with linked databases</li>
<li>Daily notes template</li>
<li>Goals tracker</li>
<li>Reading list</li>
</ul>
<p>Used it for 4 days.</p>
<p>Realized I was spending 30 minutes per day just maintaining my Notion workspace.</p>
<p><strong>The problem:</strong> Notion is infinitely flexible, which means you spend infinite time configuring it.</p>
<p>I could build anything! Which meant I had to decide what to build, how to structure it, and how to maintain it.</p>
<p>That&#39;s not a productivity tool—that&#39;s a hobby.</p>
<p><strong>Who it&#39;s actually for:</strong> People who genuinely enjoy knowledge management and database design. I still use Notion for project notes and documentation—it&#39;s great for that. Just terrible for quick &quot;remember this tomorrow&quot; tasks.</p>
<p><strong>Why it didn&#39;t stick for me:</strong> Maintaining my productivity system became another task on my todo list.</p>
<h2 id="3-google-tasks-the-one-i-kept-forgetting-existed">3. Google Tasks: The One I Kept Forgetting Existed</h2>
<p><strong>What it promises:</strong> Simple, free, integrated with Gmail and Google Calendar.</p>
<p><strong>What I experienced:</strong></p>
<p>Actually pretty solid! Clean interface, works everywhere, syncs reliably.</p>
<p>But every time I opened it, I felt... nothing.</p>
<p>It worked fine. I just never wanted to open it.</p>
<p><strong>The problem:</strong> It solved the task management problem without solving my actual workflow problem.</p>
<p>My workflow: Ideas hit me randomly throughout the day (car, gym, bed). I need to capture them instantly. Tomorrow morning, I check my email and start working.</p>
<p>Google Tasks required me to:</p>
<ol>
<li>Remember to open the app</li>
<li>Check my task list (separate from email)</li>
<li>Switch between email and tasks all morning</li>
</ol>
<p>It wasn&#39;t bad. It just didn&#39;t fit how I actually work.</p>
<p>Also, where was it? In Gmail&#39;s sidebar? As a separate app? Both? I could never remember.</p>
<p><strong>Who it&#39;s actually for:</strong> People already deep in the Google ecosystem who want lightweight task management integrated with Calendar.</p>
<p><strong>Why it didn&#39;t stick for me:</strong> I live in my email inbox. Google Tasks lived somewhere else. Eventually I stopped looking for it.</p>
<h2 id="4-todomailer-the-one-with-good-intentions-bad-execution">4. TodoMailer: The One With Good Intentions, Bad Execution</h2>
<p><strong>What it promises:</strong> Email yourself tasks, get them delivered to your inbox on schedule.</p>
<p><strong>What I experienced:</strong></p>
<p>Finally! Someone gets it. Tasks via email. Perfect.</p>
<p>Except... it sends ONE EMAIL PER TASK.</p>
<p>Added 5 tasks throughout the day. Next morning: 5 separate emails in my inbox.</p>
<p>&quot;Reply to client&quot;<br>&quot;Buy poster board&quot;<br>&quot;Call insurance&quot;<br>&quot;Schedule dentist&quot;<br>&quot;Review proposal&quot;</p>
<p>Mixed in with 12 actual emails from actual people.</p>
<p><strong>The problem:</strong> This is literally the same problem as just emailing myself from personal to work email. Individual task emails cluttering my inbox.</p>
<p>I tried it for 2 weeks hoping I&#39;d adapt. Nope. Just made my inbox more chaotic.</p>
<p><strong>Who it&#39;s actually for:</strong> People who... I honestly don&#39;t know. If you&#39;re going to get individual task emails, you might as well just email yourself and skip the middleman app.</p>
<p><strong>Why it didn&#39;t stick for me:</strong> Solved the wrong problem. I didn&#39;t want MORE emails. I wanted ONE consolidated list.</p>
<h2 id="5-microsoft-to-do-the-one-that-was-just-fine">5. Microsoft To Do: The One That Was Just... Fine</h2>
<p><strong>What it promises:</strong> Simple, free, cross-platform tasks integrated with Microsoft 365.</p>
<p><strong>What I experienced:</strong></p>
<p>Actually pretty good! Clean interface, works on Android and Windows, syncs well.</p>
<p>Used it for almost 3 weeks—my longest streak with any app besides Todoist!</p>
<p>But it still had the same core problem: It&#39;s another app to check.</p>
<p><strong>The problem:</strong> Every morning I&#39;d:</p>
<ol>
<li>Check email (where everything actually happens)</li>
<li>Try to remember to open Microsoft To Do</li>
<li>Look at my tasks</li>
<li>Switch back to email to actually do the work</li>
<li>Forget to update To Do</li>
<li>Eventually stop opening To Do</li>
</ol>
<p><strong>Who it&#39;s actually for:</strong> People already in the Microsoft ecosystem who want task integration with Outlook and Teams.</p>
<p><strong>Why it didn&#39;t stick for me:</strong> Adding tasks was fine. Remembering to check them was the problem. And I&#39;m already checking email anyway.</p>
<h2 id="what-i-learned-from-all-of-them">What I Learned From All Of Them</h2>
<p>After failing with 5 different apps, I noticed a pattern:</p>
<p><strong>They all asked me to change my workflow.</strong></p>
<ul>
<li>Check a separate app instead of email</li>
<li>Open a specific program each morning</li>
<li>Build elaborate organization systems</li>
<li>Configure notifications and settings</li>
</ul>
<p>None of them said: &quot;What&#39;s your workflow? Let&#39;s fit into that.&quot;</p>
<p>They all said: &quot;Here&#39;s OUR workflow. Adapt to it.&quot;</p>
<h2 id="what-i-actually-needed">What I Actually Needed</h2>
<p>My workflow is simple:</p>
<ol>
<li>Throughout the day, tasks pop into my head</li>
<li>I need to capture them in 5 seconds (<a href="/blog/best-ideas-on-highway">car, gym, anywhere</a>)</li>
<li>Next morning, I check email first thing</li>
<li>I want to see today&#39;s list right there</li>
</ol>
<p>That&#39;s it. I don&#39;t need projects or priorities or databases.</p>
<p>I need: <strong>Brain dump → Morning email.</strong></p>
<p>When I couldn&#39;t find that, I built TodoTomorrow. It does exactly those 4 things and nothing else.</p>
<p>Is it oversimplified? Yes.<br>Does it work for everyone? No.<br>Does it work for me? Every single day for 8 weeks now.</p>
<h2 id="the-real-question">The Real Question</h2>
<p>The best productivity app isn&#39;t the one with the most features.</p>
<p>It&#39;s the one you actually use.</p>
<p>For me, that meant building something stupidly simple that fits my actual workflow instead of trying to adopt someone else&#39;s system.</p>
<p>For you? Maybe Todoist is perfect. Maybe Notion clicks. Maybe Microsoft To Do is exactly what you need.</p>
<p>The only wrong answer is fighting with apps you don&#39;t enjoy using.</p>
<p><strong>Try TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a></strong> if you&#39;re tired of complex systems and just want tasks in your morning email. Or don&#39;t. Find what actually works for you.</p>
<hr>
<p><em>I&#39;m Joe. Built TodoTomorrow after giving up on everything else. Building in public at [<a href="https://x.com/joegaleckas">@joegaleckas</a>].</em></p>
    `
  },
  {
    slug: "why-todotomorrow-is-free",
    title: "Why TodoTomorrow Is Free (And Might Stay That Way)",
    description: "The monetization question everyone asks. Here's the honest answer about why I'm not charging (yet).",
    date: "2025-11-05",
    author: "Joe G.",
    readTime: "6 min read",
    content: `
<p><strong>The monetization question everyone asks. Here&#39;s the honest answer.</strong></p>
<p>&quot;So when are you adding the paid tier?&quot;</p>
<p>I get this question every week. From users, from other developers, from people who think I&#39;m crazy.</p>
<p>The answer: I don&#39;t know. Maybe never.</p>
<p>Let me explain why.</p>
<h2 id="the-monetization-trap">The Monetization Trap</h2>
<p>Here&#39;s what usually happens with indie apps:</p>
<p><strong>Month 1:</strong> Launch free version<br><strong>Month 2:</strong> Add &quot;Premium&quot; tier with the good features<br><strong>Month 3:</strong> Spend all your time managing payments, refunds, subscription issues<br><strong>Month 4:</strong> Realize you&#39;re making $47/month and payment processing fees are $30</p>
<p>I&#39;ve watched dozens of indie makers do this. They spend more time on Stripe integration than improving their product.</p>
<p>Then they wonder why growth stalled.</p>
<p>It&#39;s because they started optimizing for revenue before they had a product people actually wanted.</p>
<h2 id="what-i-actually-care-about-right-now">What I Actually Care About Right Now</h2>
<p>I built TodoTomorrow because <a href="/blog/5-todo-apps-i-tried">existing apps didn't work for me</a>. That's it.</p>
<p>Right now, my goals are:</p>
<ol>
<li>Make sure it works reliably for everyone</li>
<li>Get feedback from real users</li>
<li>Actually help people remember their tasks</li>
</ol>
<p>Money isn&#39;t on that list.</p>
<p>Why? Because I can&#39;t know if people actually <em>value</em> this until they use it. And they won&#39;t use it if there&#39;s a paywall.</p>
<p><strong>Free removes all friction.</strong></p>
<p>No &quot;try before you buy&quot; mental calculation. No &quot;is this worth $5/month?&quot; debate. No credit card required screen where 60% of people bounce.</p>
<p>Just: Does this solve your problem or not?</p>
<p>That&#39;s the only question that matters right now.</p>
<h2 id="the-math-that-doesn-t-work-yet">The Math That Doesn&#39;t Work Yet</h2>
<p>Let&#39;s say I added a $4.99/month premium tier tomorrow.</p>
<p>Current users: Testing phase<br>Typical conversion rate for productivity apps: 2-5%<br>Let&#39;s be generous: 5% convert</p>
<p>That&#39;s not enough to:</p>
<ul>
<li>Quit my day job</li>
<li>Hire help</li>
<li>Pay for significant infrastructure</li>
<li>Cover the mental overhead of managing subscriptions</li>
</ul>
<p>But it <em>is</em> enough to:</p>
<ul>
<li>Add complexity to the app</li>
<li>Create two-tier support burden  </li>
<li>Make users feel nickel-and-dimed</li>
<li>Distract me from building the actual product</li>
</ul>
<p><strong>Bad trade.</strong></p>
<h2 id="what-would-need-to-change">What Would Need to Change</h2>
<p>For me to add paid features, three things need to happen:</p>
<h3 id="1-hit-real-traction-first">1. Hit real traction first</h3>
<p>I need proof people actually use this daily. Not just download it, get excited for a week, then forget about it like every other todo app.</p>
<p>5,000+ active users minimum. Real retention numbers. Evidence this solves a real problem, not just a &quot;neat idea.&quot;</p>
<h3 id="2-know-what-s-worth-paying-for">2. Know what&#39;s worth paying for</h3>
<p>Right now, everything is core functionality. Add task, get morning email. That&#39;s it.</p>
<p>What premium feature would genuinely add value? Not just made-up tiers to justify pricing, but something people actually want?</p>
<ul>
<li>Multiple daily emails? (morning + afternoon)</li>
<li>Team features for families?</li>
<li>Integration with other tools?</li>
<li>Advanced customization?</li>
</ul>
<p>I don&#39;t know yet. Because I haven&#39;t talked to enough users to know what they&#39;d pay for.</p>
<h3 id="3-can-t-be-a-distraction">3. Can&#39;t be a distraction</h3>
<p>Payment processing needs to be set-it-forget-it. I can&#39;t spend 50% of my time on billing issues, subscription cancellations, and &quot;why was I charged twice?&quot; support tickets.</p>
<p>It has to make the app better, not just more profitable.</p>
<p>We&#39;re not there yet. Might never be there. And that&#39;s okay.</p>
<h2 id="the-but-how-will-you-sustain-it-question">The &quot;But How Will You Sustain It?&quot; Question</h2>
<p>Fair point. Servers cost money. Domain costs money. My time costs money.</p>
<p><strong>Current costs:</strong></p>
<ul>
<li>Supabase: Free tier (enough for current scale)</li>
<li>Resend: Free tier (1,000 emails/month per user works fine)</li>
<li>Domain: $12/year</li>
<li>Expo/React Native: Free</li>
<li><strong>Total: ~$12/year</strong></li>
</ul>
<p>As long as I stay on free tiers, this costs me basically nothing.</p>
<p>If it grows beyond free tiers? Then I have a different problem—a good problem. Enough users that monetization actually makes sense.</p>
<p>But I&#39;m not optimizing for a problem I don&#39;t have yet.</p>
<h2 id="what-this-lets-me-do">What This Lets Me Do</h2>
<p>Keeping TodoTomorrow free means I can:</p>
<p><strong>Focus on the product.</strong> No time wasted on payment flows, subscription management, or &quot;premium vs free&quot; feature decisions.</p>
<p><strong>Get real feedback.</strong> Users tell me what&#39;s broken because they want it fixed, not because they paid for it.</p>
<p><strong>Build in public.</strong> No pressure to hide metrics or pretend everything&#39;s amazing for investor/customer confidence.</p>
<p><strong>Make it actually simple.</strong> The second I add paid tiers, I&#39;m incentivized to make the free version worse to push upgrades. That&#39;s gross.</p>
<p><strong>Sleep at night.</strong> No one&#39;s paying me, so I&#39;m not worried about letting them down if I take a week off to fix bugs or rethink something.</p>
<h2 id="the-indie-hacker-heresy-">The &quot;Indie Hacker Heresy&quot;</h2>
<p>I know this goes against standard indie hacker advice:</p>
<p>&quot;Charge from day one!&quot;<br>&quot;Value your work!&quot;<br>&quot;Don&#39;t give it away!&quot;<br>&quot;You need revenue to validate!&quot;</p>
<p>All true... for some products.</p>
<p>But TodoTomorrow isn&#39;t trying to be a business yet. It&#39;s trying to be useful.</p>
<p>Maybe it becomes a business later. Maybe it stays a free tool I maintain because it solves my problem and happens to help other people too.</p>
<p>Both outcomes are fine.</p>
<h2 id="when-i-might-change-my-mind">When I Might Change My Mind</h2>
<p>If TodoTomorrow hits:</p>
<ul>
<li>10,000+ daily active users</li>
<li>30%+ day-30 retention  </li>
<li>Clear signal people would pay for specific features</li>
<li>Costs that actually require revenue</li>
</ul>
<p>Then I&#39;ll think about monetization seriously.</p>
<p>But I&#39;ll probably keep the core free forever. Add tasks, get morning email—that should never be behind a paywall.</p>
<p>Maybe premium becomes:</p>
<ul>
<li>Multiple daily emails (morning + afternoon + evening)</li>
<li>Team features for families</li>
<li>Integration with calendar/other tools</li>
<li>Advanced analytics on your productivity patterns</li>
</ul>
<p>Features that are genuinely <em>extra</em>, not core functionality dressed up as premium.</p>
<h2 id="the-real-reason-it-s-free">The Real Reason It&#39;s Free</h2>
<p>Honest answer?</p>
<p>I got tired of productivity apps that:</p>
<ul>
<li>Lock basic features behind paywalls</li>
<li>Nag you to upgrade constantly</li>
<li>Make the free version deliberately bad</li>
<li>Optimize for revenue over usefulness</li>
</ul>
<p>I wanted something that just works. No games. No pressure. No &quot;upgrade to unlock this basic feature.&quot;</p>
<p>If that means it stays free forever, cool. I already got what I wanted—a todo app I actually use.</p>
<p>Everyone else getting value from it is a bonus.</p>
<p><strong>Try TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a></strong> - Free forever (probably)</p>
<hr>
<p><em>Follow the build at [<a href="https://x.com/joegaleckas">@joegaleckas</a>] where I share real metrics, challenges, and why I&#39;m probably doing this all wrong.</em></p>
    `
  },
  {
    slug: "i-built-todo-app-in-4-weeks",
    title: "I Built a Todo App in 4 Weeks. Here's What Actually Happened",
    description: "The week-by-week story of building TodoTomorrow, including what worked, what didn't, and what I'd do differently.",
    date: "2025-11-04",
    author: "Joe G.",
    readTime: "7 min read",
    content: `
<p><strong>Spoiler: The hard part wasn&#39;t the coding.</strong></p>
<p>&quot;How long did it take you to build TodoTomorrow?&quot;</p>
<p>Everyone asks this. The answer is 4 weeks. But that&#39;s the boring answer.</p>
<p>The real answer? It took 4 weeks of building and two years of watching every other solution fail me.</p>
<h2 id="why-i-even-started-this-or-my-email-inbox-was-a-disaster-">Why I Even Started This (Or: My Email Inbox Was a Disaster)</h2>
<p>I wasn&#39;t trying to build a product. I was trying to solve a problem that wouldn&#39;t leave me alone.</p>
<p>Here&#39;s what I tried first: Emailing myself tasks. Personal email to work email. Seemed genius.</p>
<p>Every night I&#39;d fire off individual emails:</p>
<ul>
<li>&quot;REPLY TO CLIENT&quot;</li>
<li>&quot;Call dentist&quot;</li>
<li>&quot;Review proposal&quot;</li>
<li>&quot;Buy printer ink&quot;</li>
</ul>
<p>Next morning? Seven separate todo emails sitting on top of 15 actual emails from actual people.</p>
<p>I&#39;d scan my inbox triaging real work, and completely miss half my self-sent tasks buried in there. Or I&#39;d waste time opening each one individually to remember what I meant.</p>
<p>Terrible system. But at least the emails were <em>there</em>.</p>
<p>So I tried email drafts instead. Write one email with multiple tasks. Send it in the morning.</p>
<p>Except I&#39;d write the draft at 11 PM, put my phone down, and... forget to hit send.</p>
<p>Found drafts from three weeks ago sitting there. &quot;Call dentist.&quot; I&#39;d already called the dentist. And changed dentists. And moved to a new city.</p>
<p>Every solution sucked for different reasons.</p>
<p>That&#39;s when I figured: how hard could it be to build something that just... works?</p>
<h2 id="week-1-the-this-will-be-easy-week">Week 1: The &quot;This Will Be Easy&quot; Week</h2>
<p>Started with React Native + Expo. Made sense—write once, deploy everywhere.</p>
<p>Built the basic UI in 3 days. Text input. A list. Done. Felt good.</p>
<p>Then I tried to send emails.</p>
<p>Spent two full days fighting with SendGrid. Authentication. SPF records. DKIM signatures. My test emails kept landing in spam folders. Or not sending at all.</p>
<p>Turns out email delivery is way harder than it looks.</p>
<p><strong>Lesson 1:</strong> The simple stuff reveals complex problems real fast.</p>
<h2 id="week-2-the-reality-check">Week 2: The Reality Check</h2>
<p>Switched to Resend. Way better. Got emails working.</p>
<p>Then realized: If someone adds a task at 11 PM but wants the email at 7 AM, that&#39;s timezone math. What if they&#39;re traveling? What if their phone&#39;s timezone is wrong?</p>
<p>Spent a day building timezone detection. Then made it configurable—users pick their delivery time, we handle the math.</p>
<p><strong>Lesson 2:</strong> Every &quot;simple&quot; feature has edge cases hiding in it.</p>
<p>Also in Week 2: Added offline support. Because my entire use case was capturing tasks in parking lots with spotty WiFi.</p>
<p>Zustand for state management. Everything saves locally first, syncs when connection returns.</p>
<p>This ended up being the feature people mention most. &quot;Wait, it actually works offline? Like... for real?&quot;</p>
<p>Yes. For real.</p>
<h2 id="week-3-the-wait-how-does-this-actually-work-week">Week 3: The &quot;Wait, How Does This Actually Work?&quot; Week</h2>
<p>Core functionality was done. Time to test with real humans.</p>
<p>Asked 6 friends to try it. </p>
<p>Three of them immediately asked: &quot;So do the tasks disappear after you email them?&quot;</p>
<p>Huh. I hadn&#39;t thought about that at all.</p>
<p>Some people want a clean slate every day. Some people want tasks to stick around until completed.</p>
<p>Created two modes:</p>
<ul>
<li><strong>Fresh Start:</strong> Tasks clear after email (clean slate every day)</li>
<li><strong>Carry Over:</strong> Tasks persist until you complete them</li>
</ul>
<p>Thought I&#39;d kill one mode based on usage data. Turns out people split almost 50/50. Both modes stayed.</p>
<p><strong>Lesson 3:</strong> Your assumptions about how people work are always wrong.</p>
<p>Also built voice capture this week. Used the phone&#39;s built-in voice-to-text because building custom voice UI would&#39;ve taken another month I didn&#39;t have.</p>
<p>Works great. People use it way more than I expected, especially for driving.</p>
<h2 id="week-4-the-oh-god-app-stores-week">Week 4: The &quot;Oh God, App Stores&quot; Week</h2>
<p>Code was done. Now came the real work: Actually shipping this thing.</p>
<p>Google Play Store: 6 hours of forms. Screenshots. App descriptions. Privacy policy. Data safety declarations. Feature graphics. Why does your app need this permission? Justify every single API call.</p>
<p>Apple App Store: First you enroll in the Developer Program ($99/year). Then you wait for approval. Then more forms. More screenshots. Different size requirements. More explanations.</p>
<p>Decided to launch Android first, work on iOS next.</p>
<p><strong>Lesson 4:</strong> The app is maybe 40% of the work. Distribution is the other 60%.</p>
<p>Also built the marketing site (todotomorrow.com) using Lovable. Took 2 hours. Way faster than coding it myself.</p>
<p>Set up Google Analytics. Search Console. All the tracking infrastructure.</p>
<p>Launched on Product Hunt on a Tuesday morning at 12:01 AM PST.</p>
<h2 id="what-i-didn-t-build-and-why-">What I Didn&#39;t Build (And Why)</h2>
<p><strong>Recurring tasks:</strong> Would&#39;ve added 2 weeks to development. Most people&#39;s todos aren&#39;t recurring anyway—they&#39;re one-off &quot;remember to do this tomorrow&quot; tasks.</p>
<p><strong>Team features:</strong> Would&#39;ve added a month minimum. Also fundamentally changes the entire architecture. Not the problem I&#39;m solving.</p>
<p><strong>Calendar integration:</strong> Would&#39;ve added a week. But email <em>is</em> the integration point. That&#39;s the whole idea.</p>
<p><strong>Premium tiers:</strong> Would&#39;ve needed payment processing, subscription management, refund handling, tax compliance. Months of work for uncertain payoff.</p>
<p>Decided to ship free and figure out monetization later. Still figuring it out. Honestly might just keep it free.</p>
<p><strong>Lesson 5:</strong> Shipping something simple beats planning something complex.</p>
<h2 id="what-actually-worked">What Actually Worked</h2>
<p>Using Cursor AI for development sped things up massively. I&#39;m not a mobile developer—I&#39;m a guy who had a problem and learned enough to solve it.</p>
<p>Building in public on X/Twitter helped. People gave feedback before launch. Some became early users. Some told me the idea was dumb and saved me from bad features.</p>
<p>Keeping it simple meant I actually finished. Every &quot;what if we added...&quot; idea went into a &quot;maybe later&quot; list.</p>
<p>That list is long. I don&#39;t look at it.</p>
<h2 id="what-s-next-">What&#39;s Next?</h2>
<p>Honestly? Not sure.</p>
<p>I&#39;m working on iOS release now. Fixing bugs as they come up. Responding to every piece of feedback.</p>
<p>But I&#39;m not trying to build the next Todoist. I&#39;m not trying to build a venture-scale business.</p>
<p>I&#39;m trying to keep TodoTomorrow simple enough that I actually want to use it.</p>
<p>If other people find it useful too? Great. That&#39;s a bonus.</p>
<p>If not? At least I solved my own problem. No more missing my own todo emails in a cluttered inbox.</p>
<p><strong>Try TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a></strong> | Building in public at [<a href="https://x.com/joegaleckas">@joegaleckas</a>]</p>
<hr>
<p><em>Real talk: If you&#39;re thinking about building something, just start. It won&#39;t be perfect. You&#39;ll hit problems you didn&#39;t expect. But you&#39;ll learn more in 4 weeks of building than 4 months of planning.</em></p>
<p><em>What&#39;s the problem you keep trying to solve with duct tape and hope?</em></p>
    `
  },
  {
    slug: "the-productivity-app-i-actually-use",
    title: "The Productivity App I Actually Use (Because It's Stupid Simple)",
    description: "After downloading 13 productivity apps in two years, I built something so simple it actually stuck.",
    date: "2025-11-03",
    author: "Joe G.",
    readTime: "6 min read",
    content: `
<p><strong>Turns out, the problem wasn&#39;t me. It was the apps.</strong></p>
<p>I&#39;ve downloaded 13 productivity apps in the last two years.</p>
<p>Todoist. Notion. Things. Microsoft To Do. Any.do. TickTick. Remember the Milk. That one with the cute octopus mascot. A few others I&#39;ve already forgotten.</p>
<p>I used each for about 11 days before abandoning it.</p>
<p>Here&#39;s what always happened:</p>
<p><strong>Days 1-3:</strong> Wow, this is amazing! I&#39;m so organized!<br><strong>Days 4-7:</strong> Okay, which label should this go under? And what&#39;s a filter again?<br><strong>Days 8-10:</strong> I should really spend time setting this up properly...<br><strong>Day 11:</strong> <em>Opens email instead</em></p>
<h2 id="the-setup-trap">The Setup Trap</h2>
<p>Every productivity app promises to organize your life. But first, you need to organize <em>the app</em>.</p>
<p>Pick your project structure. Choose your tagging system. Decide on priority levels. Set up recurring tasks. Learn keyboard shortcuts. Integrate with calendar. Configure notifications.</p>
<p>By the time you&#39;re &quot;ready,&quot; you&#39;ve spent 3 hours on the app and zero hours on your actual work.</p>
<p>And the dirty secret? Most of that setup doesn&#39;t matter.</p>
<h2 id="the-real-problem-it-s-another-app-to-check">The Real Problem: It&#39;s Another App to Check</h2>
<p>Here&#39;s what nobody talks about:</p>
<p>Every morning I check my email. That&#39;s it. That&#39;s my starting point.</p>
<p>Email is where:</p>
<ul>
<li>My boss sends urgent requests</li>
<li>Clients follow up on projects</li>
<li>Meeting invites arrive</li>
<li>Everything that actually matters lives</li>
</ul>
<p>So when I download a new todo app, I&#39;m adding a second place to check. Now my morning routine is:</p>
<ol>
<li>Check email</li>
<li>Remember to open the todo app</li>
<li>Switch between email and todo app all morning</li>
<li>Eventually forget about the todo app</li>
</ol>
<p>Todoist tries to solve this with daily emails. But you&#39;ve seen those emails, right?</p>
<p>Gamification badges. Usage stats. &quot;Todoist tip of the day.&quot; Team feature upsells. And somewhere buried in there: &quot;You have 2 overdue tasks—click here to open Todoist.&quot;</p>
<p>It&#39;s not a task list. It&#39;s a marketing email that forces you back to the app anyway.</p>
<p>I don&#39;t want another app to check. I want my tasks in the one place I&#39;m already checking every single morning.</p>
<h2 id="what-i-actually-need-and-you-probably-do-too-">What I Actually Need (And You Probably Do Too)</h2>
<p>Here&#39;s my morning routine:</p>
<ol>
<li>Wake up</li>
<li>Check email</li>
<li>See what needs doing today</li>
<li>Do it</li>
</ol>
<p>I don&#39;t need a Kanban board. I don&#39;t need to sort by priority. I don&#39;t need a separate app that I have to remember to check.</p>
<p>I just need to know: <em>What did I say I&#39;d do today?</em></p>
<p>That&#39;s it. That&#39;s the entire feature request.</p>
<h2 id="the-app-i-actually-use-now">The App I Actually Use Now</h2>
<p>I built TodoTomorrow because I was tired of apps that think they&#39;re smarter than me.</p>
<p>It works like this:</p>
<p><strong>Throughout the day:</strong> Task pops in my head → I capture it (5 seconds) → Done</p>
<p><strong>Next morning:</strong> Get one email with everything → Work from inbox like normal</p>
<p>No projects. No tags. No priorities. No setup. No separate app to remember.</p>
<p><strong>Is this oversimplified?</strong></p>
<p>Absolutely.</p>
<p><strong>Does it work?</strong></p>
<p>I&#39;ve used it every single day for the last 8 weeks. Which is 7.5 weeks longer than any other productivity app lasted.</p>
<h2 id="three-things-that-make-it-work">Three Things That Make It Work</h2>
<h3 id="1-it-lives-where-i-already-work">1. It lives where I already work</h3>
<p>I check my email every morning. So do you. So does every other professional.</p>
<p>TodoTomorrow doesn&#39;t ask me to build a new habit—it plugs into the habit I already have.</p>
<p>My morning email has my tasks right there. Not in another app. Not behind a &quot;click here to open Todoist&quot; button. Right there in my inbox where I can work from it.</p>
<h3 id="2-capture-is-actually-fast">2. Capture is actually fast</h3>
<p>Most apps: Open → Wait for sync → Navigate to right section → Add task → Choose options → Save  </p>
<p>TodoTomorrow: Open → Type → Done</p>
<p>When an idea hits while I&#39;m driving (voice), at the gym, or half-asleep in bed, I need 5 seconds max. Otherwise I&#39;ll forget it before the app even loads.</p>
<h3 id="3-it-s-one-list">3. It&#39;s one list</h3>
<p>Tuesday morning. I open my email. There&#39;s my TodoTomorrow email with 4 tasks.</p>
<p>I don&#39;t need to:</p>
<ul>
<li>Check multiple project folders</li>
<li>Filter by due date</li>
<li>Sort by priority</li>
<li>Switch between views</li>
<li>Remember to open a separate app</li>
</ul>
<p>One email. One list. Everything I said I&#39;d do today.</p>
<h2 id="what-it-doesn-t-do-on-purpose-">What It Doesn&#39;t Do (On Purpose)</h2>
<p>No team features<br>No project management<br>No recurring tasks<br>No priority levels<br>No Gantt charts<br>No integrations with 47 other apps<br>No gamification<br>No productivity score<br>No daily digest that&#39;s actually a marketing email</p>
<p>If you need any of that, use something else. Seriously.</p>
<p>TodoTomorrow is for people who want the absolute minimum viable todo app that works with their email workflow instead of against it.</p>
<h2 id="the-three-day-test">The Three-Day Test</h2>
<p>If you&#39;ve abandoned 5+ productivity apps like me, try this:</p>
<p>Use TodoTomorrow for exactly 3 days. Don&#39;t try to organize it or optimize it—just capture tasks when they pop up.</p>
<p>If on day 4 you&#39;re still using it without thinking about it? It&#39;s working.</p>
<p>If on day 4 you&#39;ve already forgotten about it? Then you&#39;re like everyone else with every other app, and at least this one is free.</p>
<p><strong>Download TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a>.</strong></p>
<p>It takes 30 seconds to set up. If it takes longer than that, I&#39;ve already failed.</p>
<hr>
<p><em>I&#39;m building this in public at [<a href="https://x.com/joegaleckas">@joegaleckas</a>]. Currently helping people who were tired of checking another app when they already live in email.</em></p>
    `
  },
  {
    slug: "ive-sent-myself-847-task-emails",
    title: "I've Sent Myself 847 Task Emails. Here's What I Learned",
    description: "The email-yourself strategy makes perfect sense. Until it completely doesn't.",
    date: "2025-11-02",
    author: "Joe G.",
    readTime: "7 min read",
    content: `
<p><strong>The email-yourself strategy makes perfect sense. Until it completely doesn&#39;t.</strong></p>
<p>Quick question: How many draft emails do you have right now?</p>
<p>Go check. I&#39;ll wait.</p>
<p>I just checked mine. Seventeen drafts. Half of them are tasks I meant to do weeks ago: &quot;CALL PLUMBER,&quot; &quot;reply to Mark,&quot; &quot;Buy batteries.&quot;</p>
<p>Every single one represents a moment where I thought <em>&quot;I should email this to myself so I don&#39;t forget&quot;</em>... and then forgot anyway.</p>
<h2 id="the-email-yourself-strategy-makes-perfect-sense-in-theory-">The Email-Yourself Strategy Makes Perfect Sense (In Theory)</h2>
<p>The logic is bulletproof:</p>
<ol>
<li>I check my email first thing every morning</li>
<li>If I email myself a task, it&#39;ll be right there in my inbox</li>
<li>I can&#39;t possibly miss it</li>
<li>Genius!</li>
</ol>
<p>Except it fails. Constantly. For three stupid reasons.</p>
<h2 id="why-it-fails-the-three-problems">Why It Fails: The Three Problems</h2>
<h3 id="problem-1-individual-emails-get-buried">Problem 1: Individual emails get buried</h3>
<p>I tried this first. Every time I thought of a task:</p>
<ul>
<li>Personal email to work email: &quot;Reply to client proposal&quot;</li>
<li>Personal email to work email: &quot;Schedule dentist appointment&quot;  </li>
<li>Personal email to work email: &quot;Buy birthday card for Emma&quot;</li>
<li>Personal email to work email: &quot;Review Q4 budget&quot;</li>
</ul>
<p>Next morning? Seven task emails sitting on top of 15 actual emails from actual people.</p>
<p>I&#39;d scan my inbox triaging real work—boss&#39;s urgent request, client follow-up, meeting invite—and completely miss half my self-sent tasks buried in there.</p>
<p>Or I&#39;d waste 5 minutes opening each one individually to remember what I meant by &quot;THAT THING.&quot;</p>
<p>Terrible system.</p>
<h3 id="problem-2-drafts-are-invisible-and-you-forget-to-send-them-">Problem 2: Drafts are invisible (and you forget to send them)</h3>
<p>Okay, so individual emails cluttered my inbox. Solution: Write ONE email draft with multiple tasks. Send it in the morning.</p>
<p>Better plan, right?</p>
<p>Here&#39;s what actually happened:</p>
<p>11:47 PM. Lying in bed. Remember three things for tomorrow. Open email. Start draft to myself:</p>
<ul>
<li>Reply to Sarah about proposal</li>
<li>Buy poster board for Emma&#39;s project  </li>
<li>Call insurance about claim</li>
</ul>
<p>Add another task. Oh wait, one more. Okay, done.</p>
<p>Put phone down. Fall asleep.</p>
<p>Morning: Check my inbox. No task email. Because it&#39;s sitting in my Drafts folder, which I literally never check.</p>
<p>Most of us don&#39;t hit send. We write the email, get interrupted (kid needs something, red light turns green, podcast gets interesting), and... that&#39;s it.</p>
<p>The draft sits there. Invisible. Useless.</p>
<p>Found drafts from three weeks ago. &quot;Call dentist.&quot; I already called the dentist. And switched dentists. And moved to a different state.</p>
<h3 id="problem-3-even-sent-emails-disappear-in-the-noise">Problem 3: Even sent emails disappear in the noise</h3>
<p>Let&#39;s say you actually remember to send the email. Great!</p>
<p>But so did your boss. And that newsletter you keep meaning to unsubscribe from. And Amazon with your shipping update. And your dentist confirming your appointment. And LinkedIn telling you someone viewed your profile.</p>
<p>By morning, your self-sent task email is buried under 12 other emails.</p>
<p>You scan your inbox, triage the &quot;important&quot; stuff, and completely miss your own reminder sitting there at 6 AM between the marketing email and the calendar invite.</p>
<p>You&#39;re not working from a todo list—you&#39;re excavating tasks from an email avalanche.</p>
<h2 id="what-actually-works-instead">What Actually Works Instead</h2>
<p>After 847 failed task emails (yes, I counted), I realized the problem isn&#39;t email itself. It&#39;s the <em>timing</em> and <em>format</em>.</p>
<p><strong>What breaks:</strong></p>
<ul>
<li>Individual emails sent at random times → inbox clutter</li>
<li>Draft emails you forget to send → invisible tasks</li>
<li>Mixed with actual emails from other people → gets lost</li>
</ul>
<p><strong>What works:</strong></p>
<ul>
<li>All tasks captured in one place throughout the day → no juggling</li>
<li>Delivered as ONE email at the exact right time → predictable</li>
<li>Separate from your actual inbox chaos → impossible to miss</li>
</ul>
<p>This is why I built TodoTomorrow. Not because email is bad—because email is actually perfect for this. We just need to use it differently.</p>
<h2 id="how-it-actually-works-in-real-life">How It Actually Works in Real Life</h2>
<p><strong>Yesterday:</strong></p>
<p>9:15 AM (gym parking lot): Open TodoTomorrow, type &quot;Call insurance about claim&quot;<br>2:30 PM (between meetings): &quot;Buy poster board for Emma&#39;s project&quot;<br>6:45 PM (making dinner): Use voice, &quot;Email Sarah about proposal&quot;<br>10:20 PM (in bed): &quot;Research electricians for outlet issue&quot;</p>
<p>Each one took 5 seconds. No &quot;should I draft this or send it?&quot; No &quot;which email address?&quot; No &quot;what subject line?&quot;</p>
<p>Just: Open app. Type. Done.</p>
<p><strong>This morning:</strong></p>
<p>7:00 AM: ONE email arrives with all four tasks in a clean list.</p>
<p>I scan it while drinking coffee, know exactly what my day looks like, and work from that email.</p>
<p>No excavating buried emails. No checking draft folders. No &quot;wait, did I email myself that?&quot;</p>
<h2 id="the-part-that-surprised-me">The Part That Surprised Me</h2>
<p>I thought the killer feature would be the single batched email.</p>
<p>But users keep telling me it&#39;s the <em>capture speed</em> that changed their behavior.</p>
<p><strong>Old way (email draft):</strong></p>
<ol>
<li>Open email app (5 sec)</li>
<li>Start new message (3 sec)</li>
<li>Type your own email address (4 sec)</li>
<li>Think of a subject line (3 sec)</li>
<li>Type the task (5 sec)</li>
<li>Remember to actually send it (2 sec... or never)
Total: 22 seconds if you remember. Infinite seconds if you forget.</li>
</ol>
<p><strong>TodoTomorrow:</strong></p>
<ol>
<li>Open app (2 sec)</li>
<li>Type task (3 sec)
Total: 5 seconds</li>
</ol>
<p>That difference matters when you&#39;re at a red light, or waiting for your daughter at gymnastics, or lying in bed half-asleep.</p>
<p>If it&#39;s not fast, you won&#39;t do it. And then you&#39;re back to forgetting.</p>
<h2 id="does-this-actually-solve-your-problem-">Does This Actually Solve Your Problem?</h2>
<p>Try this test:</p>
<p>In the last week, how many times have you thought <em>&quot;I should remember to do that tomorrow&quot;</em> and then forgot?</p>
<p>If the answer is more than zero, you have the same problem I had.</p>
<p>TodoTomorrow might help. Or maybe you&#39;ll find your own solution. Either way, stop drafting emails at 11 PM and forgetting to send them. It doesn&#39;t work.</p>
<p><strong>Download TodoTomorrow on <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Android</a> or <a href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345">iOS</a>.</strong></p>
<hr>
<p><em>I&#39;m Joe. I built this because I was tired of forgetting tasks. Building in public at [<a href="https://x.com/joegaleckas">@joegaleckas</a>]. What&#39;s your task capture nightmare?</em></p>
    `
  },
  {
    slug: "i-wasted-4-months-on-todoist",
    title: "I Wasted 4 Months on Todoist. Then I Built Something Stupidly Simple",
    description: "Here's what happens when productivity apps become the problem they're supposed to solve.",
    date: "2025-11-01",
    author: "Joe G.",
    readTime: "6 min read",
    content: `
<p><strong>Here&#39;s what happens when productivity apps become the problem they&#39;re supposed to solve.</strong></p>
<p>I&#39;m in my car. Daughter&#39;s gymnastics class, 20 minutes left. And it hits me—three things I absolutely cannot forget tomorrow.</p>
<p>So I grab my phone and... what? Open Todoist?</p>
<p>That means:</p>
<ul>
<li>Pick the right project</li>
<li>Assign a priority level</li>
<li>Set a due date</li>
<li>Choose tags</li>
</ul>
<p>By the time I&#39;m done, she&#39;s walked out and I&#39;ve missed her entire routine through the window.</p>
<p>This wasn&#39;t a one-time thing. It kept happening. Driving home, podcast reminds me about the dentist. Lying in bed, remember that critical client email. At the gym between sets.</p>
<p>The best ideas for tomorrow always hit when I&#39;m <em>not</em> at my desk.</p>
<p>But capturing them felt like work.</p>
<h2 id="the-todoist-trap-or-how-i-spent-more-time-organizing-than-doing-">The Todoist Trap (Or: How I Spent More Time Organizing Than Doing)</h2>
<p>Look, Todoist is legitimately impressive. My buddy Mike? Guy&#39;s a wizard with it. Projects nested in projects, custom filters, labels for everything. He&#39;s got a whole <em>system</em>.</p>
<p>Me? I spent four months trying to be Mike.</p>
<p>Every single task became a decision tree:</p>
<ul>
<li>&quot;Wait, is this Work → Current Projects or Work → Active?&quot;</li>
<li>&quot;P1 or P2 priority?&quot;</li>
<li>&quot;Should I add the &#39;quick-win&#39; label?&quot;</li>
</ul>
<p>And then their &quot;daily digest&quot; email? It&#39;s productivity theater. Gamification badges. Usage stats. Marketing for team features. Buried somewhere: &quot;you have tasks, click here to open the app.&quot;</p>
<p>That&#39;s not a task list. That&#39;s an engagement metric disguised as help.</p>
<p>I kept thinking: <em>I&#39;m doing this wrong. I need to learn the system better.</em></p>
<p>Turns out? Maybe the system is just too much system.</p>
<h2 id="what-i-actually-needed-spoiler-way-less-">What I Actually Needed (Spoiler: Way Less)</h2>
<p>Here&#39;s my real workflow:</p>
<p>Throughout the day—car, gym, bed, wherever—ideas for tomorrow pop up. I need to capture them in 5 seconds flat. In the morning, I check email before anything else anyway (don&#39;t we all?). </p>
<p>That&#39;s when I want one thing: A list. Just &quot;here&#39;s what you said you&#39;d do today.&quot;</p>
<p>No projects. No priorities. No Kanban boards. No second-guessing if something is P2 or P3.</p>
<p>Just: Brain dump → Morning email.</p>
<h2 id="so-i-built-it">So I Built It</h2>
<p>TodoTomorrow does exactly one thing. Turns scattered thoughts into one organized morning email.</p>
<p><strong>The flow:</strong></p>
<ol>
<li>Open app, type task (or use voice if you&#39;re driving), done. Five seconds.</li>
<li>Next morning at whatever time you pick, ONE email with everything.</li>
<li>Work from your inbox like you already do.</li>
</ol>
<p>No organizing. No prioritizing. No inbox spam from individual task emails.</p>
<p>I use it everywhere now:</p>
<ul>
<li>Gymnastics pickup: &quot;Buy poster board for school project&quot;</li>
<li>Gym parking lot: &quot;Call insurance about claim&quot;  </li>
<li>Driving (voice): &quot;Reply to Sarah&#39;s proposal&quot;</li>
<li>Before bed: &quot;Research Denver electricians&quot;</li>
</ul>
<p>All of it lands in one email at 7 AM.</p>
<h2 id="the-feature-that-changes-everything">The Feature That Changes Everything</h2>
<p>Todoist <em>does</em> send a daily email. But look at what you actually get:</p>
<p>Productivity goals with gamification badges. Usage stats showing your limits. Tips of the day. Team management upsells. And somewhere buried in there? A vague &quot;you have 2 overdue tasks&quot; with a button to... open the Todoist app.</p>
<p>It&#39;s not a task list. It&#39;s a marketing email disguised as productivity.</p>
<p>You can&#39;t actually <em>work</em> from that email. You have to context-switch back to the app. Which defeats the entire point of getting an email in the first place.</p>
<p>TodoTomorrow sends one thing: Your tasks. Plain text. In your inbox. Ready to work from.</p>
<p>No gamification. No upsells. No &quot;open the app to see what you forgot.&quot; Just the list you need to start your day.</p>
<h2 id="real-talk-who-this-isn-t-for">Real Talk: Who This Isn&#39;t For</h2>
<p>If you manage complex projects with teams, you need Todoist. Or Asana. Something with power.</p>
<p>Also not for you if organizing tasks is your jam. Some people love building systems. Mike thrives on it.</p>
<p>This ain&#39;t that.</p>
<h2 id="who-should-try-it">Who Should Try It</h2>
<p>You should test TodoTomorrow if:</p>
<ul>
<li>You just want to remember tomorrow&#39;s tasks</li>
<li>You already live in your email inbox</li>
<li>You capture ideas throughout the day (not just at a desk)</li>
<li>You&#39;re done fighting with complex apps</li>
<li>You want something that takes zero time to learn</li>
</ul>
<h2 id="it-s-free-for-real-">It&#39;s Free (For Real)</h2>
<p>No premium tiers. No paywalls. No credit card required.</p>
<p>I&#39;m not monetizing yet. Maybe never. Right now I just want to solve this problem for people who have it.</p>
<p><strong>Download for Android:</strong> <a href="https://play.google.com/store/apps/details?id=com.todotomorrow.app">Google Play</a><br><strong>iOS:</strong> Coming soon</p>
<p>Five-second task capture. One morning email. That&#39;s the whole pitch.</p>
<h2 id="the-three-day-test">The Three-Day Test</h2>
<p>Use it for exactly three days. If on day four you&#39;re back to manually emailing yourself tasks or wrestling with complex apps?</p>
<p>I failed.</p>
<p>But if you find yourself using it without thinking about it? That&#39;s when you know it works.</p>
<p>Next time you&#39;re sitting outside gymnastics with three things you can&#39;t forget tomorrow, you&#39;ll get why I built this.</p>
<hr>
<p><em>Building this in public on X [<a href="https://x.com/joegaleckas">@joegaleckas</a>]. Currently helping people who were as frustrated as I was. What&#39;s your task capture nightmare?</em></p>
    `
  }
];
